import React from "react";
import { Box, Tooltip, Button } from "@mui/material";
import { useDrop } from "react-dnd";
import { resolveBoothIcon } from "../../utils/IconMap";
import { useLocalization } from "../../context/LocalizationContext";

// Amber = pending, red = rejected. No dot for approved/null.
const STATUS_DOT = {
  pending:  '#f59e0b',
  rejected: '#ef4444',
};

const BoothSpot = ({
  spot,
  booth,
  onRequestChange,
  isMobile = false,
  selectedBooth = null,
  isReserved = false,   // true when another button has a pending approval targeting this spot
  onDismiss = null,     // callback(buttonId, approvalId) — only provided for rejected booths
  isFixed = false,      // true for position 23 (stadium) — display only, no drag-drop or click
}) => {
  const { locale, t } = useLocalization();

  const approvalStatus = booth?.approvalStatus ?? null;
  const dotColor = STATUS_DOT[approvalStatus] ?? null;

  // Block drops when:
  //   1. Spot is reserved by another pending approval (isReserved)
  //   2. Spot is already occupied by a different booth (booth != null)
  //   3. The dragged button is already at this spot (same-spot no-op)
  //
  // IMPORTANT: deps array is required so that canDrop re-evaluates on each render.
  // Without it the factory closure captures stale prop values from the first render.
  const [{ isOver, canDropHere }, drop] = useDrop(
    () => ({
      accept: "ICON",
      canDrop: (item) =>
        !isFixed &&
        !isReserved &&
        !booth &&
        item?.positionId !== spot.id,
      drop: (item) => onRequestChange("drop", spot.id, item),
      collect: (monitor) => ({
        isOver:      !!monitor.isOver(),
        canDropHere: monitor.canDrop(),
      }),
    }),
    [isFixed, isReserved, booth, spot.id, onRequestChange],
  );

  const boothName = booth
    ? booth.localizedPairs?.values?.find((v) => v.localeId === locale)?.value ||
      booth.localizedPairs?.values?.[0]?.value ||
      t(booth.localizedPairs?.key, '') ||
      ''
    : "";

  const boothIcon = resolveBoothIcon(booth, "btn_tt1");

  const boothContent = booth ? (
    <img
      src={boothIcon}
      alt="booth-icon"
      style={{ width: "60%", height: "60%", objectFit: "contain" }}
    />
  ) : null;

  const isAvailableSlot = isMobile && !!selectedBooth && !booth;
  const isOccupiedSlot  = isMobile && !!booth;

  const handleMobileTap = () => {
    if (isReserved && !booth) return; // reserved empty spot — block
    if (isAvailableSlot) {
      // Block same-slot placement (selected booth is already in this slot)
      if (selectedBooth?.positionId === spot.id) return;
      onRequestChange("drop", spot.id, selectedBooth);
    } else if (isOccupiedSlot) {
      onRequestChange("remove", spot.id, booth);
    }
  };

  const tooltipParts = [];
  if (isReserved && !booth)          tooltipParts.push("Reserved — another button has a pending approval targeting this spot");
  else if (booth && !isReserved)     tooltipParts.push(boothName);
  if (approvalStatus === "pending")  tooltipParts.push("Pending approval — waiting for manager review");
  if (approvalStatus === "rejected") tooltipParts.push("Rejected — dismiss to clear");
  if (isAvailableSlot)               tooltipParts.push("Tap to place here");
  const tooltipTitle = tooltipParts.join(" · ") || undefined;

  // Border color logic:
  //   isOver + canDropHere → yellow dashed (valid drop target)
  //   isOver + !canDropHere → red dashed (blocked: occupied / reserved / same spot)
  //   isReserved (empty) → amber dashed (reserved by another button's pending)
  //   pending approval on current booth → amber solid
  //   rejected → red solid
  //   available mobile slot → white dashed
  //   default → transparent
  let borderStyle = "2px solid transparent";
  if (isOver && canDropHere)              borderStyle = "2px dashed yellow";
  else if (isOver && !canDropHere)        borderStyle = "2px dashed #ef4444";
  else if (isReserved && !booth)          borderStyle = "2px dashed #f59e0b";
  else if (approvalStatus === "pending")  borderStyle = "2px solid #f59e0b";
  else if (approvalStatus === "rejected") borderStyle = "2px solid #ef4444";
  else if (isAvailableSlot)              borderStyle = "2px dashed rgba(255,255,255,0.85)";

  const bgColor = isAvailableSlot
    ? "rgba(10,50,109,0.35)"
    : isReserved && !booth
      ? "rgba(245,158,11,0.15)"
      : "#0a326d";

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      arrow
      disableHoverListener={!tooltipTitle}
    >
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        {/* Approval status dot — top-right corner */}
        {dotColor && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: dotColor,
              border: "2px solid #fff",
              zIndex: 2,
            }}
          />
        )}

        <Box
          ref={isMobile || isFixed ? null : drop}
          onClick={isFixed ? undefined : (isMobile ? handleMobileTap : (booth ? () => onRequestChange("remove", spot.id, booth) : undefined))}
          sx={{
            width: { xs: 44, sm: 56, md: 70 },
            height: { xs: 44, sm: 56, md: 70 },
            borderRadius: "50%",
            bgcolor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: borderStyle,
            cursor: isFixed ? "default" : (isMobile
              ? (isAvailableSlot || isOccupiedSlot ? "pointer" : "default")
              : (booth ? "pointer" : "default")),
            transition: "transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
            "&:hover": (!isFixed && (booth || isAvailableSlot))
              ? { transform: "scale(1.05)", bgcolor: isAvailableSlot ? "rgba(10,50,109,0.5)" : "#0d47a1" }
              : {},
            animation: isAvailableSlot ? "teleportPulse 1.4s ease-in-out infinite" : "none",
            "@keyframes teleportPulse": {
              "0%, 100%": { boxShadow: "0 0 0 0 rgba(255,255,255,0.5)" },
              "50%":       { boxShadow: "0 0 0 5px rgba(255,255,255,0)" },
            },
          }}
        >
          {boothContent}
        </Box>

        {/* Dismiss button — only shown for rejected approvals */}
        {approvalStatus === "rejected" && onDismiss && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(booth.id, booth.approvalId);
            }}
            sx={{
              position: "absolute",
              bottom: -22,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "0.6rem",
              px: 0.5,
              py: 0,
              minWidth: 0,
              bgcolor: "#ef4444",
              "&:hover": { bgcolor: "#dc2626" },
              whiteSpace: "nowrap",
              zIndex: 3,
            }}
          >
            Dismiss
          </Button>
        )}
      </Box>
    </Tooltip>
  );
};

export default BoothSpot;
